/**
 * Numbered tour-stop placement.
 *
 * The map is a short itinerary card fitted to the whole route, so towns a few
 * kilometres apart land on top of each other. We keep the geographic
 * coordinate (the stem still points at the stop) and fan the disc in *pixels*
 * until each number is readable. Zooming in leaves a harmless 32px nudge.
 */
import type { TourExperienceStop, TourStopKind } from '../types/tours.types'
import { TOUR_MAP_FIT_PADDING, TOUR_MAP_HEIGHT_PHONE } from './tour-map-layout'

export const TOUR_STOP_MARKER_DISC = 28
export const TOUR_STOP_MARKER_STEM = 6
export const TOUR_STOP_MARKER_HEIGHT = TOUR_STOP_MARKER_DISC + TOUR_STOP_MARKER_STEM - 2
export const TOUR_STOP_MIN_GAP = TOUR_STOP_MARKER_DISC + 4

export interface TourStopBounds {
  ne: [number, number]
  sw: [number, number]
}

export interface TourStopViewport {
  width: number
  height: number
  padding: number
}

export interface PlacedTourStopMarker {
  id: string
  number: number
  kind: TourStopKind
  label: string
  longitude: number
  latitude: number
  offsetX: number
  offsetY: number
}

const DEFAULT_VIEWPORT: TourStopViewport = {
  width: 640,
  height: TOUR_MAP_HEIGHT_PHONE,
  padding: TOUR_MAP_FIT_PADDING,
}

export function orderTourStops(stops: readonly TourExperienceStop[]): TourExperienceStop[] {
  return stops
    .filter(
      (stop) =>
        Number.isFinite(stop.latitude) &&
        Number.isFinite(stop.longitude) &&
        Math.abs(stop.latitude) <= 90 &&
        Math.abs(stop.longitude) <= 180,
    )
    .slice()
    .sort((a, b) => a.position - b.position)
}

export function layoutTourStopMarkers(
  stops: readonly TourExperienceStop[],
  bounds: TourStopBounds,
  viewport: TourStopViewport = DEFAULT_VIEWPORT,
): PlacedTourStopMarker[] {
  const ordered = orderTourStops(stops)

  const placed: PlacedTourStopMarker[] = []
  const occupied: Array<{ x: number; y: number }> = []

  for (const [index, stop] of ordered.entries()) {
    const anchor = projectStop(stop.longitude, stop.latitude, bounds, viewport)
    let offset = { x: 0, y: 0 }
    for (let attempt = 0; attempt < 24; attempt += 1) {
      offset = fanOffset(attempt)
      const disc = discCenter(anchor.x + offset.x, anchor.y + offset.y)
      if (occupied.every((other) => distance(disc, other) >= TOUR_STOP_MIN_GAP)) {
        occupied.push(disc)
        break
      }
    }
    placed.push({
      id: stop.id,
      number: index + 1,
      kind: stop.kind,
      label: stop.label,
      longitude: stop.longitude,
      latitude: stop.latitude,
      offsetX: Math.round(offset.x),
      offsetY: Math.round(offset.y),
    })
  }
  return placed
}

export function tourStopFill(
  kind: TourStopKind,
  number: number,
  total: number,
): 'coral' | 'purple' {
  if (kind === 'pickup' || kind === 'destination' || number === 1 || number === total) {
    return 'coral'
  }
  return 'purple'
}

function projectStop(
  longitude: number,
  latitude: number,
  bounds: TourStopBounds,
  viewport: TourStopViewport,
): { x: number; y: number } {
  const innerWidth = Math.max(1, viewport.width - viewport.padding * 2)
  const innerHeight = Math.max(1, viewport.height - viewport.padding * 2)
  const spanLng = Math.max(1e-6, bounds.ne[0] - bounds.sw[0])
  const spanLat = Math.max(1e-6, bounds.ne[1] - bounds.sw[1])
  return {
    x: viewport.padding + ((longitude - bounds.sw[0]) / spanLng) * innerWidth,
    y: viewport.padding + ((bounds.ne[1] - latitude) / spanLat) * innerHeight,
  }
}

function discCenter(anchorX: number, anchorY: number): { x: number; y: number } {
  return {
    x: anchorX,
    y: anchorY - TOUR_STOP_MARKER_STEM - TOUR_STOP_MARKER_DISC / 2,
  }
}

function fanOffset(attempt: number): { x: number; y: number } {
  if (attempt === 0) return { x: 0, y: 0 }
  const ring = Math.ceil(attempt / 8)
  const slot = (attempt - 1) % 8
  const angle = (slot * Math.PI) / 4 + Math.PI / 8
  const radius = ring * TOUR_STOP_MIN_GAP
  return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius }
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
